import cn from 'classnames';
import { m } from 'framer-motion';
import { useEffect, useState } from 'react';
import { AllTopsWidget, Button, Chevron, Container, Flex, LabelText, Link, NavItemTypography, ReferWidget } from '@components';
import { MenuGroupV2, SubMenuGroupV2 } from '@ts/index';
import variables from '@styles/export.module.scss';
import BYOBWidget from '../BYOBWidget';
import LensesWidget from '../LensesWidget';
import styles from './AllTopsMenu.module.scss';

interface AllTopsMenuV2Props {
	menuGroup: MenuGroupV2;
	openSubMenuCallback?: (value: { value: boolean; menuGroup: SubMenuGroupV2 }) => void;
	activeItem: SubMenuGroupV2 | null;
	disableMargin?: boolean;
	disableButtonStyle?: boolean;
	widgets: { showReferWidget?: boolean; showBYOBWidget?: boolean; showAllTopsWidget?: boolean; isLensesMenu?: boolean };
}
export const AllTopsMenuV2 = ({
	menuGroup,
	openSubMenuCallback = null,
	activeItem,
	disableMargin = true,
	disableButtonStyle = true,
	widgets: { showReferWidget, showBYOBWidget, showAllTopsWidget, isLensesMenu },
}: AllTopsMenuV2Props) => {
	const [showSubMenu, setShowSubMenu] = useState(false);
	useEffect(() => {
		setShowSubMenu(!!activeItem);
	}, [activeItem]);
	return (
		<Container
			className={cn(styles.container, {
				[styles['lenses-container']]: isLensesMenu,
				[styles['disable-margin']]: disableMargin,
				[styles['disable-button-style']]: disableButtonStyle,
			})}
		>
			{showAllTopsWidget && <AllTopsWidget />}
			{!activeItem && (
				<ul style={{ marginLeft: '-0.3rem', minWidth: '21rem' }} key={`${menuGroup.handle}`}>
					{menuGroup.subMenuGroupV2.map((item, index) => {
						const { handle, link, text } = item;
						const collectionTag = 'collectionTag' in item ? item.collectionTag : undefined;
						const subMenuGroup = 'subMenuGroup' in item ? item.subMenuGroup : undefined;

						return (
							<>
								{subMenuGroup ? (
									<>
										<li tabIndex={0} className={styles['list-item']}>
											<Button
												linkStyle
												aria-haspopup='true'
												aria-controls={`subnav-${handle}`}
												size='small'
												fullWidth
												fullHeight
												onClick={() => {
													openSubMenuCallback?.({ value: true, menuGroup: item as SubMenuGroupV2 });
												}}
											>
												<Flex fullWidth className={styles['indicator-container']}>
													{item.text}
													<Chevron direction='right' />
												</Flex>
											</Button>
										</li>
									</>
								) : (
									<li key={`${handle}-${index}`} className={styles['list-item']} data-handle={handle}>
										{collectionTag && (
											<>
												<div data-type={collectionTag} className={styles['item-tag']} />
												<LabelText data-type={collectionTag} className={styles['item-tag-label']}>
													{collectionTag}
												</LabelText>
											</>
										)}
										<Link href={link ?? '#'} small>
											{text}
										</Link>
									</li>
								)}
							</>
						);
					})}
				</ul>
			)}
			<m.ul
				initial={'hidden'}
				animate={showSubMenu ? 'visible' : 'hidden'}
				variants={{
					hidden: { opacity: 0, x: 100, transition: { duration: 0.3, ease: 'easeOut' } },
					visible: { opacity: 1, x: 0, transition: { duration: 0.3, ease: 'easeOut' } },
				}}
				key={`sub-menu-group-${activeItem?.handle}`}
				className={cn(styles['sub-menu-group'], {
					[styles['sub-menu-group--hidden']]: !activeItem,
				})}
				aria-labelledby={activeItem?.text}
				style={{ marginLeft: '-0.3rem', minWidth: '21rem' }}
			>
				<li tabIndex={0} className={styles['list-item']}>
					<Button
						linkStyle
						size='small'
						fullWidth
						fullHeight
						centerContent
						extraClasses={styles['mobile-back-btn-v2']}
						onClick={() => {
							setShowSubMenu(false);
							setTimeout(() => {
								openSubMenuCallback?.({
									value: true,
									menuGroup: null,
								});
							}, 100);
						}}
					>
						<Chevron
							direction='left'
							width={20}
							height={20}
							extraStyles={{ display: 'inline', color: variables.blue, margin: 0, padding: 0 }}
						/>
						<NavItemTypography style={{ fontSize: '1.4rem', lineHeight: '2.1rem', padding: '0 4px' }}>
							{activeItem?.text}
						</NavItemTypography>
					</Button>
				</li>
				{activeItem?.subMenuGroup?.map((item, index) => {
					const isBYOBMenuL = item.handle === 'build-your-own-bundle';
					return (
						<li
							key={`${item.handle}-${index}`}
							className={styles['list-item']}
							data-handle={item.handle}
							{...(isBYOBMenuL ? { 'data-nav-byob-link': true } : {})}
						>
							{item?.collectionTag && (
								<>
									<div data-type={item.collectionTag} className={styles['item-tag']} />
									<LabelText data-type={item.collectionTag} className={styles['item-tag-label']}>
										{item.collectionTag}
									</LabelText>
								</>
							)}
							<Link href={item.link ?? '#'} small>
								{item.text}
							</Link>
						</li>
					);
				})}
			</m.ul>

			{showBYOBWidget && <BYOBWidget />}
			{showReferWidget && <ReferWidget />}
			{isLensesMenu && <LensesWidget />}
		</Container>
	);
};
